// Utils
import http from '../utils/services/http';
import localStorageService from '../utils/services/localStorage/localStorage';
import { getDeviceId } from '../device/deviceService';
import globalLogger from '../logger/logger';

// Services
import { removeRememberedBetterAuthSessionUser, removeCurrentUserToken } from './authService';
import { resetBetterAuthSessionMigrationStatus } from './betterAuthSessionMigrationService';
import { clearInboxReturnState } from '../inbox/returnState/inboxReturnStateService';

// Selectors
import { getCurrentUserId } from '../user/currentUserSelector';

// Constants
import { METHODS } from '../../common/utils/http/httpConstants';
import { AUTH_APP_LOCALE_LOCAL_STORAGE_KEY } from './localisation/authLocaleConstants';
import { THEME_LOCAL_STORAGE_KEY } from '../theme/themeService';
import { OT_LAST_ACTIVE_GROUPS_LOCAL_STORAGE } from '../../common/utils/cookie/mnCookieConstants';
import { DEBUG_LOCAL_STORAGE_KEY } from '../debug/debugConstants';
import { DND_3D_EFFECTS_LOCAL_STORAGE_KEY } from '../interfaceSettings/interfaceSettingsService';
import { GRID_SIZE_LOCAL_STORAGE_KEY } from '../utils/grid/gridSizeStorageService';
import { LAST_APP_MODE_KEY } from '../platform/utils/platformAppModeUtils';
import { VIRTUAL_KEYBOARD_HEIGHT_STORAGE_KEY } from '../utils/keyboard/useSaveVirtualKeyboardHeightToLocalStorage';
import { APP_REVIEW_DATA_KEY } from '../mobile/mobileAppReviewService';
import {
    LAST_REFRESH_TIME_LOCAL_STORAGE_KEY,
    RESET_APP_CACHE_LOCAL_STORAGE_KEY,
} from '../offline/appLocalCacheStorageService';
import * as AUTH_ACTION_TYPES from './authConstants';

const logger = globalLogger.createChannel('auth.logout');

// Browser/device preferences and operational state must survive account changes.
const PRESERVED_MILANOTE_LOCAL_STORAGE_KEYS = new Set([
    AUTH_APP_LOCALE_LOCAL_STORAGE_KEY,
    THEME_LOCAL_STORAGE_KEY,
    OT_LAST_ACTIVE_GROUPS_LOCAL_STORAGE,
    DEBUG_LOCAL_STORAGE_KEY,
    DND_3D_EFFECTS_LOCAL_STORAGE_KEY,
    GRID_SIZE_LOCAL_STORAGE_KEY,
    LAST_APP_MODE_KEY,
    VIRTUAL_KEYBOARD_HEIGHT_STORAGE_KEY,
    APP_REVIEW_DATA_KEY,
    LAST_REFRESH_TIME_LOCAL_STORAGE_KEY,
    RESET_APP_CACHE_LOCAL_STORAGE_KEY,
]);

/**
 * Clears Milanote storage by default so new account state cannot silently survive logout.
 */
const clearMilanoteLocalStorage = (): void => {
    const userStateKeys = Object.keys(localStorageService).filter(
        (key) =>
            key.startsWith('milanote.') &&
            !PRESERVED_MILANOTE_LOCAL_STORAGE_KEYS.has(key) &&
            !key.startsWith(`${DEBUG_LOCAL_STORAGE_KEY}.`),
    );

    userStateKeys.forEach((key) => localStorageService.removeItem(key));
};

export const clearLegacyUserSession = () =>
    http({
        method: METHODS.GET,
        url: 'auth/clear-session',
    });

export const clearBetterAuthUserSession = () =>
    http({
        method: METHODS.POST,
        url: 'auth-v2/sign-out',
        skipAuthHeader: true,
        withCredentials: true,
    });

/**
 * Clears all client-side per-user session state. Shared by explicit logout and the forced-logout
 * (lapsed Better Auth session) path so both leave the browser in the same clean state.
 */
export const clearLocalUserSessionState = (): void => {
    clearMilanoteLocalStorage();
    clearInboxReturnState();
    resetBetterAuthSessionMigrationStatus();
    removeCurrentUserToken();
    removeRememberedBetterAuthSessionUser();
};

// FIXME - Instead of performing this logic here, potentially move it to middleware?
//  Try to fix this so that the notifications can be unsubscribed from without causing
//  the electron build to fail
/**
 * Same as logout in authActions however this doesn't call unsubscribeFromPushNotifications
 * because that was causing the electron app to fail building.
 */
export const simpleLogout = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const userId = getCurrentUserId(state);

    // Both sessions are cleared unconditionally, because the client cannot reliably tell which it
    // holds: `/upgrade-better-auth-session` sets the Better Auth cookie *before* the client verifies
    // it, so a migration that failed part-way leaves a live cookie behind alongside the legacy token
    // it was replacing. Signing out of Better Auth is idempotent — `better-auth/api/routes/sign-out`
    // deletes the session only when a signed cookie is present, but always clears the cookie and
    // always returns `{ success: true }` — so the redundant call is harmless.
    // Once legacy auth is retired, the `clearLegacyUserSession` call below can simply be deleted.
    try {
        await clearLegacyUserSession();
    } catch (error) {
        logger.warn('Failed to clear legacy auth session during logout', error);
    }

    try {
        await clearBetterAuthUserSession();
    } catch (error) {
        // Logged rather than blocking the logout: the local session state below is cleared either
        // way, so a failure here strands the cookie but still logs the user out of this client,
        // whereas refusing to log out leaves them stuck in an app they have asked to leave.
        //
        // Sign-out never fails merely because there was no cookie, so every failure logged here is a
        // real one — worth chasing in Rollbar rather than sampling away.
        logger.warn('Failed to clear Better Auth session during logout', error);
    }

    clearLocalUserSessionState();

    dispatch({
        type: AUTH_ACTION_TYPES.LOGOUT,
        deviceId: getDeviceId(),
        userId,
        sync: true,
    });
};
